import type { ReactNode } from "react";
import { MiAutocomplete, MiButton, MiList } from "@miplatform/components";
import {
  ALL,
  BUTTON_VARIANTS,
  PAGINATION,
  PERMISSIONS,
} from "@miplatform/constants";
import { useRouter } from "@miplatform/hooks";
import {
  IAccountResponse,
  IBidResponse,
  ICommunitiesResponse,
  ISeriesGroupResponse,
  ISeriesResponse,
} from "@miplatform/types";
import { getValueFormat } from "@miplatform/utils";
import { usePermissions, useSession } from "@mibuildweb/hooks";
import { Box } from "@mui/material";
import { SectionInfo } from "../../components/SectionInfo/SectionInfo";
import { StatusChip } from "../../components/StatusChip/StatusChip";
import { BID_REQUEST_TYPE } from "../CreateBidRequest/constants/constants";
import { BidFilter, GetBidsParams } from "../types";
import { BidCard } from "./BidCard";
import { BidDueDateChip } from "./BidDueDateChip";

export function BidList({
  data,
  setData,
  dataProvider,
  filters,
  refreshKey,
  search,
  accounts,
  communities,
  seriesGroups,
  series,
  assigneeOptions = [],
  getDaysUntilDue,
  handleAssignBid,
  handleStartTask,
}: Readonly<BidListProps>) {
  const { params, navigate } = useRouter();
  const { hasPermission } = usePermissions();
  const { session } = useSession();

  const canAssignBids = hasPermission(PERMISSIONS.PLAN.ASSIGN);
  const canStartBids = hasPermission(PERMISSIONS.PLAN.UPDATE);
  const isOnNewTab = params.tab === "new";
  const isOnSetupTab = params.tab === "setup";

  const getSections = () => {
    const fieldset = [
      {
        renderValue: (bid: IBidResponse) => {
          const status =
            bid.planStatus === "Assignment" ? "New" : bid.planStatus;

          const subtitle = bid.bidRequest
            ? `${(bid.bidRequestIndex ?? 0) + 1}/${bid.bidRequest.planCount}${
                bid.bidRequest.name ? ` | ${bid.bidRequest.name}` : ""
              }`
            : undefined;

          return (
            <SectionInfo
              data={{ ...bid, subtitle }}
              chip={<StatusChip status={status} />}
              disableNewChip
            />
          );
        },
        flexSize: 2,
      },
      {
        label: "Customer",
        field: "builderId",
        renderValue: (bid: IBidResponse) =>
          accounts.find((a) => a.id === bid.builderId)?.name || "--",
      },
      {
        label: "Community",
        field: "communityId",
        renderValue: (bid: IBidResponse) =>
          communities.find((c) => c.id === bid.communityId)?.name || "--",
      },
      {
        label: "Series",
        field: "seriesId",
        renderValue: (bid: IBidResponse) =>
          series.find((s) => s.id === bid.seriesId)?.name || "--",
      },
      {
        label: "Due Date",
        renderValue: (bid: IBidResponse) => {
          const daysUntilDue = getDaysUntilDue(bid);

          return (
            <Box display="flex" flexDirection="column">
              {getValueFormat(bid.bidRequest?.dueDate, "Date")}
              {daysUntilDue <= 2 && (
                <BidDueDateChip daysUntilDue={daysUntilDue} />
              )}
            </Box>
          );
        },
      },
      {
        label: "Requested On",
        renderValue: (bid: IBidResponse) =>
          getValueFormat(bid.bidRequest?.requestDate, "Date"),
      },
    ];
    if (!canAssignBids) {
      fieldset.push({
        label: "Assignee",
        renderValue: (bid: IBidResponse) =>
          assigneeOptions.find((a) => a.value === bid.assigneeId)?.label ||
          "--",
      });
    }

    return { fieldset };
  };

  const handleNavigate = ({ id }: { id: string }) => {
    navigate(`/estimating/bids/create/${id}`);
  };

  const getGroupedCriteria = filters.groupBy
    ? (item: IBidResponse) => {
        if (filters.groupBy === "bidRequestId") {
          if (!item.bidRequest) return "";
          return `${item.bidRequest.name} - ${
            BID_REQUEST_TYPE[item.bidRequest.requestType]
          }`;
        }
        if (filters.groupBy === "builderId") {
          return accounts.find((a) => a.id === item.builderId)?.name || "";
        }
        if (filters.groupBy === "communityId") {
          return communities.find((c) => c.id === item.communityId)?.name || "";
        }
        if (filters.groupBy === "seriesGroupId") {
          return (
            seriesGroups.find((sg) => sg.id === item.seriesGroupId)?.name || ""
          );
        }
        if (filters.groupBy === "seriesId") {
          return series.find((s) => s.id === item.seriesId)?.name || "";
        }
        return "";
      }
    : undefined;

  const getGroupedParamsCriteria = filters?.groupBy
    ? (item: IBidResponse, array: IBidResponse[]) => {
        let mostRecentModifiedOn = item.modifiedOn || "";
        let leastRecentModifiedOn = item.modifiedOn || "";

        for (const current of array) {
          const currentDate = current.modifiedOn || "";
          if (currentDate > mostRecentModifiedOn) {
            mostRecentModifiedOn = currentDate;
          }
          if (currentDate < leastRecentModifiedOn) {
            leastRecentModifiedOn = currentDate;
          }
        }

        return {
          dueDate: item.bidRequest?.dueDate || "",
          requestDate: item.bidRequest?.requestDate || "",
          mostRecentModifiedOn,
          leastRecentModifiedOn,
        };
      }
    : undefined;

  const getAssignmentActions = (item: IBidResponse) => {
    const canStartTask = canStartBids && session?.userId === item.assigneeId;

    const actions: ReactNode[] = [];

    if ((isOnNewTab || isOnSetupTab) && canAssignBids && handleAssignBid) {
      actions.push(
        <MiAutocomplete
          label="Assignee"
          value={
            assigneeOptions.find(
              (option) => option.value === item.assigneeId
            ) || null
          }
          options={assigneeOptions.filter((option) => option.value !== ALL)}
          onChange={(_, option) => handleAssignBid(item, option?.value || null)}
          isOptionEqualToValue={(option, value) => option.value === value.value}
          disableClearable={!item.assigneeId}
          textFieldProps={{
            size: "small",
            sx: { minWidth: 200, background: "white" },
          }}
        />
      );
    }

    if (isOnNewTab && canStartBids && handleStartTask) {
      actions.push(
        <MiButton
          key={`start-task-${item.id}`}
          title="Start Task"
          variant={BUTTON_VARIANTS.CONTAINED}
          onClick={() => handleStartTask(item)}
          disabled={!canStartTask}
        />
      );
    }

    return actions.length ? (
      <Box
        display="flex"
        alignItems="center"
        gap={2}
        onClick={(e) => e.stopPropagation()}
      >
        {actions}
      </Box>
    ) : null;
  };

  const listData =
    filters.planStatus !== ALL
      ? data.filter((item) => {
          if (filters.planStatus && item.planStatus !== filters.planStatus) {
            return false;
          }
          return true;
        })
      : data;

  return (
    <MiList
      paginationMode={PAGINATION.SERVER}
      defaultPageSize={25}
      data-testid="bids-List"
      CardComponent={BidCard}
      cardProps={{
        sections: [getSections()],
        onClick: !isOnNewTab ? handleNavigate : undefined,
        renderItemActions: getAssignmentActions,
      }}
      viewType="card"
      groupedMode={filters.groupBy !== "None"}
      pinGroupKeyToTop="Unknown"
      showGroupCount
      groupedCriteria={getGroupedCriteria}
      groupedDataSort={(a: [string, GroupedData], b: [string, GroupedData]) => {
        const [aName, aData] = a;
        const [bName, bData] = b;

        switch (filters.sortBy) {
          case "requestDate desc":
            return bData.requestDate.localeCompare(aData.requestDate);
          case "requestDate asc":
            return aData.requestDate.localeCompare(bData.requestDate);
          case "dueDate desc":
            return bData.dueDate.localeCompare(aData.dueDate);
          case "dueDate asc":
            return aData.dueDate.localeCompare(bData.dueDate);
          case "modifiedOn desc":
            return bData.mostRecentModifiedOn.localeCompare(
              aData.mostRecentModifiedOn
            );
          case "modifiedOn asc":
            return aData.leastRecentModifiedOn.localeCompare(
              bData.leastRecentModifiedOn
            );
          case "None":
            return aName.toLowerCase().localeCompare(bName.toLowerCase());
          default:
            return 0;
        }
      }}
      groupedParamsCriteria={getGroupedParamsCriteria}
      groupedDataInternalSort={(a: IBidResponse, b: IBidResponse) => {
        return a.bidRequestIndex != null && b.bidRequestIndex != null
          ? a.bidRequestIndex - b.bidRequestIndex
          : a.name.toLowerCase().localeCompare(b.name.toLowerCase());
      }}
      refresh={refreshKey ?? filters}
      data={listData}
      setData={setData}
      dataProvider={dataProvider}
      search={search}
    />
  );
}

interface BidListProps {
  data: IBidResponse[];
  setData: (data: IBidResponse[]) => void;
  dataProvider: (params: GetBidsParams) => Promise<void>;
  filters: BidFilter;
  refreshKey?: string;
  search: string;
  accounts: IAccountResponse[];
  communities: ICommunitiesResponse[];
  seriesGroups: ISeriesGroupResponse[];
  series: ISeriesResponse[];
  assigneeOptions?: { label: string; value: string }[];
  getDaysUntilDue: (bid: IBidResponse) => number;
  handleAssignBid?: (bid: IBidResponse, assigneeId: string | null) => void;
  handleStartTask?: (bid: IBidResponse) => void;
}

interface GroupedData {
  data: IBidResponse[];
  dueDate: string;
  requestDate: string;
  mostRecentModifiedOn: string;
  leastRecentModifiedOn: string;
}
